"use client";

/**
 * Reusable Action Buttons for Admin Pages
 * 
 * These buttons provide a consistent UI for common actions across admin pages.
 * 
 * @example
 * // Add Button - Primary blue button with Plus icon
 * <AddButton onClick={handleAddItem}>
 *   Thêm sản phẩm
 * </AddButton>
 * 
 * // Refresh Button - Secondary gray button with RefreshCw icon
 * <RefreshButton onClick={loadItems}>
 *   Làm mới
 * </RefreshButton>
 * 
 * // With permission check
 * <AddButton onClick={handleAdd} requiredPermission="products.create">
 *   Thêm sản phẩm
 * </AddButton>
 * 
 * // With disabled state
 * <AddButton onClick={handleAdd} disabled={isLoading}>
 *   Thêm
 * </AddButton>
 * 
 * // With custom className
 * <RefreshButton onClick={refresh} className="custom-class">
 *   Refresh
 * </RefreshButton>
 */

import { Plus, RefreshCw } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import styles from "./ActionButtons.module.css";

interface AddButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  requiredPermission?: string | string[]; // Permission code cần thiết để hiển thị button này
}

interface RefreshButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  requiredPermission?: string | string[]; // Permission code cần thiết để hiển thị button này
}

export function AddButton({ onClick, children, disabled = false, className = "", requiredPermission }: AddButtonProps) {
  const { hasPermission } = usePermissions();

  // Nếu có requiredPermission và user không có quyền, không render button
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return null;
  }

  return (
    <button 
      className={`${styles.btn} ${styles.btnPrimary} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      <Plus size={20} />
      {children}
    </button>
  );
}

export function RefreshButton({ onClick, children, disabled = false, className = "", requiredPermission }: RefreshButtonProps) {
  const { hasPermission } = usePermissions();

  // Nếu có requiredPermission và user không có quyền, không render button
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return null;
  }

  return (
    <button 
      className={`${styles.btn} ${styles.btnSecondary} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      <RefreshCw size={20} />
      {children}
    </button>
  );
}

